import type { ComponentType, ReactNode } from 'react';
import {
  Clock, Calendar, Megaphone, Plane, CalendarDays, FileCheck,
  User, Mail, BookOpen, Bell, CheckSquare, MessageSquare, Film,
  // 판 5 — 시안 화면 F(풀사이즈 6종) · L(업무 위젯 4종)
  CalendarRange, Users, Image as ImageIcon, BarChart3, Zap, FolderOpen, ListTodo, Sparkles,
  // 날씨 (2026-09-21)
  Sun,
  type LucideIcon,
} from '../../../icons';
import type { WidgetSpan } from '../../../store/dashboardStore';
import { AttendanceWidget, AttendanceSummaryWidget } from './widgets/AttendanceWidgets';
import { CalendarMiniWidget, ScheduleDetailWidget } from './widgets/CalendarWidgets';
import { LeaveWidget, ProfileWidget } from './widgets/HrWidgets';
import { NoticesWidget, NotificationsWidget, ClassroomWidget } from './widgets/InfoWidgets';
import { NoticeSlideWidget } from './widgets/NoticeSlideWidget';
import { BroadcastWidget } from './widgets/BroadcastWidget';
import { ApprovalWidget, MailWidget, TasksWidget, ChatsWidget } from './widgets/WorkWidgets';
import { WeatherWidget } from './widgets/WeatherWidget';
import {
  WeekTimelineWidget, RoomStatusWidget, PhotoWallWidget, KpiStripWidget, NoticeBannerWidget,
} from './widgets/FullsizeWidgets';
import {
  TeamPresenceWidget, QuickLaunchWidget, RecentDocsWidget, TodayTodoWidget, WeekSummaryWidget,
} from './widgets/WorkDetailWidgets';

/**
 * 위젯 레지스트리 — 위젯의 **단일 SSOT**.
 *
 * 보드(#2a·#2b)가 그릴 컴포넌트도, 새 대시보드 모달(#2d)이 고르게 할 카드 목록도
 * 전부 이 표 하나에서 나온다. 위젯을 추가할 때 손댈 곳은 여기 한 줄뿐이고,
 * 보드/모달/기본 배치는 자동으로 따라온다(목록을 두 군데 적어 어긋나는 사고 차단).
 *
 * `preview` 는 #2d 카드의 미니 미리보기 — 실제 위젯을 렌더하면 모달을 여는 순간
 * 모든 위젯의 API 를 동시에 때리게 되므로, 데이터 없이 그리는 도형으로 둔다.
 */

/**
 * 위젯 분류 — 고르기 창의 왼쪽 묶음 (2026-09-09 · 시안 화면 D 의 `data-cat`).
 *
 * ★ 위젯이 스물넷이 되면 **한 덩어리 목록은 못 읽는다.** 시안이 처음부터 넷으로 나눠 두었다.
 */
export type WidgetCat = 'work' | 'plan' | 'talk' | 'info';

export const WIDGET_CAT_LABEL: Record<WidgetCat, string> = {
  work: '업무',
  plan: '일정',
  talk: '소통',
  info: '정보',
};

export interface WidgetDef {
  key: string;
  label: string;
  /** 고르기 창의 묶음. 안 주면 «정보». */
  cat?: WidgetCat;
  /** #2d 카드 부제 — 위젯이 무엇을 보여주는지 한 줄. */
  hint: string;
  icon: LucideIcon;
  defaultSpan: WidgetSpan;
  Component: ComponentType;
  preview: () => ReactNode;
}

/* ── #2d 미니 미리보기 도형 (데이터 요청 없음) ─────────────────────── */
const previewClock = () => <span className="dw-mini__clock">14:32</span>;
const previewCal = () => (
  <span className="dw-mini__cal" aria-hidden>
    {Array.from({ length: 21 }, (_, i) => <i key={i} className={i === 9 ? 'is-on' : undefined} />)}
  </span>
);
const previewRows = (onIndex = 0) => () => (
  <span className="dw-mini__rows" aria-hidden>
    {[0, 1, 2].map((i) => <i key={i} className={i === onIndex ? 'is-on' : undefined} data-w={i} />)}
  </span>
);
const previewRing = () => <span className="dw-mini__ring" aria-hidden />;
const previewLine = () => (
  <span className="dw-mini__line" aria-hidden>
    <i className="is-on" /><s className="is-on" /><i className="is-on" /><s /><i />
  </span>
);
const previewBars = () => (
  <span className="dw-mini__bars" aria-hidden>
    {[45, 70, 95, 60].map((h, i) => (
      <i key={h} className={i === 2 ? 'is-on' : undefined} style={{ height: `${h}%` }} />
    ))}
  </span>
);
const previewIcon = (Icon: LucideIcon) => () => <Icon size={22} aria-hidden />;
/** 공지 슬라이드 — 도는 판이라는 것을 «점 세 개» 로 말한다(그림 한 장이 아니다). */
/** 사내 방송 — 「재생」 하나로 말한다(움직이는 그림을 미리보기에서 돌리지 않는다). */
const previewFilm = () => (
  <span className="dw-mini__film" aria-hidden><i /></span>
);
/* 판 5 — 통짜 위젯의 미니 도형 셋. 데이터 요청 없이 «모양»만 말한다. */
const previewWeek = () => (
  <span className="dw-mini__week" aria-hidden>
    {[30, 55, 40, 70, 45].map((h, i) => (
      <i key={h} className={i === 3 ? 'is-on' : undefined} style={{ height: `${h}%` }} />
    ))}
  </span>
);
const previewTiles = () => (
  <span className="dw-mini__tiles" aria-hidden>
    {Array.from({ length: 6 }, (_, i) => <i key={i} className={i === 1 ? 'is-on' : undefined} />)}
  </span>
);
const previewBanner = () => <span className="dw-mini__banner" aria-hidden />;
const previewSlide = () => (
  <span className="dw-mini__slide" aria-hidden>
    <i className="is-on" /><i /><i />
  </span>
);

export const WIDGET_DEFS: WidgetDef[] = [
  { key: 'attendance', cat: 'work', label: '근태', hint: '출·퇴근 · 근무시간', icon: Clock, defaultSpan: 4, Component: AttendanceWidget, preview: previewClock },
  { key: 'calendar', cat: 'plan', label: '캘린더', hint: '월간 일정 미니뷰', icon: Calendar, defaultSpan: 4, Component: CalendarMiniWidget, preview: previewCal },
  { key: 'notices', cat: 'talk', label: '공지사항', hint: '사내 공지 목록', icon: Megaphone, defaultSpan: 4, Component: NoticesWidget, preview: previewRows(0) },
  // 공지 슬라이드 (2026-09-08 · migration 172) — 위의 `notices`(목록)는 **그대로 둔다**.
  // 목록으로 보고 싶은 회사가 있고, 슬라이드는 «따로 만드는 것» 이지 게시글의 다른 모습이 아니다.
  { key: 'notice-slide', cat: 'talk', label: '공지 슬라이드', hint: '이미지 · 텍스트가 한 줄로', icon: Megaphone, defaultSpan: 8, Component: NoticeSlideWidget, preview: previewSlide },
  // 사내 방송 (2026-09-08 · migration 174) — 통합설정에서 위젯에 건 채널의 최신 영상.
  { key: 'broadcast', cat: 'talk', label: '사내 방송', hint: '채널의 최신 영상 · 이어보기', icon: Film, defaultSpan: 4, Component: BroadcastWidget, preview: previewFilm },
  { key: 'leave', cat: 'work', label: '휴가', hint: '잔여 연차 · 신청 내역', icon: Plane, defaultSpan: 4, Component: LeaveWidget, preview: previewRing },
  { key: 'schedule', cat: 'plan', label: '일정 상세', hint: '시간대별 일정', icon: CalendarDays, defaultSpan: 4, Component: ScheduleDetailWidget, preview: previewRows(1) },
  { key: 'approval', cat: 'work', label: '전자결재', hint: '결재선 · 상태', icon: FileCheck, defaultSpan: 4, Component: ApprovalWidget, preview: previewLine },
  { key: 'profile', cat: 'info', label: '내 프로필', hint: '프로필 · 상태 · 휴가', icon: User, defaultSpan: 4, Component: ProfileWidget, preview: previewIcon(User) },
  { key: 'mail', cat: 'talk', label: '메일', hint: '안읽음 · 최근 메일', icon: Mail, defaultSpan: 4, Component: MailWidget, preview: previewIcon(Mail) },
  { key: 'attendance-summary', cat: 'work', label: '근태 관리', hint: '출퇴근 · 이번 달 요약', icon: Clock, defaultSpan: 6, Component: AttendanceSummaryWidget, preview: previewBars },
  // ★ 고르는 자리의 설명도 함께 고친다 (2026-09-16) — 카드는 「미개설」이라는데 목록은
  //   「사내 교육 진도」라고 하면, 진도를 기대하고 얹은 사람이 빈 카드를 받는다.
  { key: 'classroom', cat: 'info', label: '내 강의실', hint: '미개설 — 사내 교육', icon: BookOpen, defaultSpan: 4, Component: ClassroomWidget, preview: previewIcon(BookOpen) },
  { key: 'notifications', cat: 'talk', label: '알림', hint: '최근 알림', icon: Bell, defaultSpan: 4, Component: NotificationsWidget, preview: previewRows(2) },
  { key: 'tasks', cat: 'work', label: '내 태스크', hint: '진척률 · 마감', icon: CheckSquare, defaultSpan: 4, Component: TasksWidget, preview: previewBars },
  { key: 'chats', cat: 'talk', label: '최근 대화', hint: '최근 활동 대화방', icon: MessageSquare, defaultSpan: 4, Component: ChatsWidget, preview: previewIcon(MessageSquare) },

  /*
   * 판 5 — 시안 화면 F(풀사이즈) · L(업무 위젯) (2026-09-09).
   *
   * ★★★ 시안 화면 F ⑥ 「사내 방송」은 여기 없다 — 위의 `broadcast` 가 **같은 것**이다(판 3).
   * ★★ 화면 L 의 ①~④(내 정보 · 휴가 · 근태 · 메일)도 위에 이미 있다.
   * ★ 폭 기본값은 시안이 그린 대로다 — 넓어야 뜻이 통하는 것은 8 · 12 로 선다.
   */
  { key: 'week-timeline', cat: 'plan', label: '주간 타임라인', hint: '이 주 일정을 시간대로', icon: CalendarRange, defaultSpan: 8, Component: WeekTimelineWidget, preview: previewWeek },
  { key: 'room-status', cat: 'plan', label: '회의실 현황', hint: '오늘 · 지금 비어 있는 자원', icon: Users, defaultSpan: 8, Component: RoomStatusWidget, preview: previewTiles },
  // ★ 시안(화면 F ③)의 이름은 「팀 갤러리」다 — 「갤러리」로 줄여 두었더니 그 위젯이 없는 줄 알았다.
  { key: 'photo-wall', cat: 'info', label: '팀 갤러리', hint: '고른 폴더의 사진', icon: ImageIcon, defaultSpan: 8, Component: PhotoWallWidget, preview: previewTiles },
  { key: 'kpi-strip', cat: 'work', label: '한눈에', hint: '결재 · 근무 · 연차 · 프로젝트', icon: BarChart3, defaultSpan: 12, Component: KpiStripWidget, preview: previewBars },
  { key: 'notice-banner', cat: 'talk', label: '사내 공지', hint: '필독 공지 하나를 크게', icon: Megaphone, defaultSpan: 12, Component: NoticeBannerWidget, preview: previewBanner },
  { key: 'team-presence', cat: 'work', label: '팀 근무 현황', hint: '오늘 팀원의 근무 상태', icon: Users, defaultSpan: 4, Component: TeamPresenceWidget, preview: previewRows(0) },
  { key: 'quick-launch', cat: 'work', label: '빠른 실행', hint: '자주 하는 일 바로가기', icon: Zap, defaultSpan: 12, Component: QuickLaunchWidget, preview: previewTiles },
  { key: 'recent-docs', cat: 'info', label: '최근 문서', hint: '최근에 연 문서', icon: FolderOpen, defaultSpan: 4, Component: RecentDocsWidget, preview: previewRows(1) },
  { key: 'today-todo', cat: 'work', label: '오늘 할 일', hint: '결재 · 마감 · 일정을 한 줄로', icon: ListTodo, defaultSpan: 4, Component: TodayTodoWidget, preview: previewRows(2) },
  // 시안 화면 A 의 「이번 주 요약」 — 판 5 에서 빠져 있었다(2026-09-09).
  { key: 'week-summary', cat: 'work', label: '이번 주 요약', hint: '회의 · 근무 · 처리한 결재', icon: Sparkles, defaultSpan: 4, Component: WeekSummaryWidget, preview: previewBars },
  // 날씨 (2026-09-21 · 시안 v4 확정) — 대시보드 맨 위 «고정 줄»과 **같은 부품**을 쓴다.
  //   기본은 내 사업장(없으면 회사) · 폭 1/3. 「어디 날씨」는 그 자리의 설정이다.
  { key: 'weather', cat: 'info', label: '날씨', hint: '지금 기온 · 하늘 · 강수', icon: Sun, defaultSpan: 4, Component: WeatherWidget, preview: previewIcon(Sun) },
];

const BY_KEY = new Map(WIDGET_DEFS.map((d) => [d.key, d]));

/** 저장된 배치에 남아 있는 미지의 key 는 undefined 로 돌아온다(렌더 시 건너뜀). */
export function getWidgetDef(key: string): WidgetDef | undefined {
  return BY_KEY.get(key);
}
